import { Button, Dropdown, Flexbox, Icon, List, useViewPreferences } from "st-app";
import { requireRoleDashboardContext } from "../RoleDashboardContext";
import { StaffRole, roleDashboard as api } from 'sis-app/api'
import { For, Show, createEffect, createMemo, createSignal, onMount } from "solid-js";
import styles from '../sis-role-dashboard.module.css'
import { createProxySignal } from "common";
import { GroupAction } from "./GroupAction";

const RoleConfig: Record<StaffRole, { name: string; icon: string; color: string }> = {
    [StaffRole.AttendanceClerk]: { name: "Attendance Clerk", icon: "fa-clipboard-list", color: '--header-indigo' },
    [StaffRole.Counselor]: { name: "Counselor", icon: "fa-user-friends", color: '--header-orange' },
    [StaffRole.DistrictAdmin]: { name: "District Admin", icon: "fa-user-shield", color: '--header-magenta' },
    [StaffRole.DistrictStateReporting]: { name: "District State Reporting", icon: "fa-chart-line", color: '--header-green' },
    [StaffRole.Nurse]: { name: "Nurse", icon: "fa-user-nurse", color: '--header-azure' },
    [StaffRole.Registrar]: { name: "Registrar", icon: "fa-id-badge", color: '--header-red' },
    [StaffRole.SchoolAdministrator]: { name: "School Administrator", icon: "fa-school", color: '--header-amber' },
    [StaffRole.SupportAgent]: { name: "Support Agent", icon: "fa-headset", color: '--header-teal' },
    [StaffRole.SupportSupervisor]: { name: "Support Supervisor", icon: "fa-user-tie", color: '--header-cyan' },
    [StaffRole.SupportUser]: { name: "Support User", icon: "fa-life-ring", color: '--header-lime' },
    [StaffRole.Any]: { name: 'Unknown', icon: '', color: '' },
    [StaffRole.Unknown]: { name: 'Unknown', icon: '', color: '' },
};

export function Header() {
    const ctx = requireRoleDashboardContext();
    const roles = createMemo(() => {
        const actions = ctx.configuration()?.actions;
        if (actions) {
            const roles = new Set(actions.filter(a => a.role !== StaffRole.Any).map(a => a.role));
            return roles;
        }
        return new Set<StaffRole>();
    });
    const otherRoles = createMemo(() => Array.from(roles()).filter(r => r !== ctx.selectedRole()));

    const [selectedRole, setSelectedRole] = createProxySignal<StaffRole | undefined>(
        () => ctx.selectedRole(),
        (val) => ctx.setSelectedRole(val ?? StaffRole.Unknown)
    );

    let scroller!: HTMLDivElement;
    let el!: HTMLDivElement;
    onMount(() => {
        if (roles().size > 1) {
            // on z-scroll, flip to the prev/next role
            el.addEventListener(
                "wheel",
                (ev) => {
                    ev.preventDefault();

                    const inc = (ev.deltaY > 0 ? 1 : -1);
                    select(inc);
                },
                { passive: false }
            );
        }
    });

    function select(inc: number) {
        const roleArr = Array.from(roles());

        let idx = roleArr.indexOf(ctx.selectedRole());
        idx = (idx + inc + roleArr.length) % roleArr.length;
        ctx.setSelectedRole(roleArr[idx]);
    }

    return <>
        <Flexbox as="header" dir="vertical" class={styles.headerContainer} >
            <Flexbox ref={el} dir="horizontal" class={styles.header} data-staff-role={StaffRole[ctx.selectedRole()]} >
                <Flexbox dir="vertical">
                    <Flexbox.Item as="h1">{RoleConfig[ctx.selectedRole()]?.name}</Flexbox.Item>
                    <Show when={ctx.userName()}>
                        <Flexbox.Item as="address">Welcome back, {ctx.userName()}</Flexbox.Item>
                    </Show>
                </Flexbox>

                <Show when={roles().size > 1}>
                    <Flexbox ref={scroller} dir="horizontal" middle style="margin-left: 10px; padding-left:10px; border-left: 1px solid var(--st-input-border)">
                        <Flexbox dir="vertical">
                            <Button label="Previous Role" type="link" style="padding: 2px 3px" onClick={() => select(-1)} >
                                <Icon src="fas fa-caret-up" />
                            </Button>
                            <Button label="Next Role" type="link" style="padding: 2px 3px" onClick={() => select(1)} >
                                <Icon src="fas fa-caret-down" />
                            </Button>
                        </Flexbox>
                    </Flexbox>
                </Show>

                <Flexbox.Item auto></Flexbox.Item>
                <GroupAction id="Revelation.QRG" size="medium" />
            </Flexbox>

            <Show when={roles().size > 1}>
                <div class={styles.tabContainer} style="z-index: 1">
                    <Flexbox as="ul" class={styles.tabs} role="tablist" dir="horizontal">
                        <For each={Array.from(roles())}>{role => {
                            const rc = RoleConfig[role];
                            const isActive = createMemo(() => role === selectedRole());

                            return <li classList={{ [styles.active]: isActive() }} style={`--active-color: var(${rc.color})`}>
                                <Flexbox as="a" dir="horizontal" padded role="tab" onClick={() => setSelectedRole(role)} >
                                    <Icon src={`fas ${rc.icon}`} />
                                    <span>{rc.name}</span>
                                </Flexbox>
                            </li>
                        }}</For>
                    </Flexbox>
                </div>
            </Show>
        </Flexbox>
    </>
}
